// Libraries
import { cloneDeep } from "lodash";

// Utils
import { or } from "@Common/helpers/sonar";

// Types
import { TOneMicroStore, TSelectedKiosk, TSwitchableItem } from "@/Settings/types/administrationTools/utils";
import { IKiosk, IKmsStore, ISaleKiosk } from "@Settings/types/administrationTools/model";
import { getSortedOptions } from "@/Layouts/components/InputComponents/SmartSelector/utils";

function getKioskOptions(stores: IKmsStore[]) {
	if (!stores?.length) {
		return [];
	}

	return stores.reduce((acc, store) => {
		const { saleKiosks, serviceKiosks, name: storeName, id: storeId } = store;

		const newSaleKiosks = saleKiosks.map(({ id, name, selected }) => ({
			label: `(${id}) ${name}`,
			storeName,
			storeId,
			id,
			name,
			selected,
		}));

		const newServiceKiosks = serviceKiosks.map(({ id, name }) => ({
			label: `(${id}) ${name}`,
			storeName,
			storeId,
			id,
			name,
			selected: true,
		}));

		return [...acc, ...newSaleKiosks, ...newServiceKiosks];
	}, [] as { label: string; storeName: string; storeId: number; id: number; name: string; selected: boolean }[]);
}

function defineKiosksTypes(stores: TOneMicroStore[]): IKmsStore[] {
	if (!stores?.length) {
		return [];
	}

	return stores.map<IKmsStore>(({ kiosks, ...restStoreProperties }) => {
		const saleKiosks: ISaleKiosk[] = kiosks.map((kiosk) => ({ ...kiosk, selected: false }));

		return { ...restStoreProperties, saleKiosks, serviceKiosks: [] };
	});
}

function updateSelectedKiosksInStores(kmsStores: IKmsStore[], selectedKiosks: TSelectedKiosk[]) {
	const newKmsStores = cloneDeep(kmsStores);
	const resetKmsStores = resetSelectedSalesKiosksInStores(newKmsStores);

	return resetKmsStores.map((store) => {
		const { id: storeId, saleKiosks, serviceKiosks, name } = store;

		const selectedKiosksByStore = selectedKiosks.filter((kiosk) => kiosk.storeId === storeId).map((kiosk) => kiosk.kioskId);

		if (!selectedKiosksByStore?.length) {
			return { ...store, serviceKiosks: [] };
		}

		const selectedSaleKiosks = saleKiosks.map((kiosk) => {
			if (selectedKiosksByStore.includes(kiosk.id)) {
				return { ...kiosk, selected: true };
			}

			return { ...kiosk, selected: false };
		});

		const { newSelectedServiceKiosks, oldSelectedServiceKiosks } = serviceKiosks.reduce(
			(acc, kiosk: IKiosk) => {
				if (selectedKiosksByStore.includes(kiosk.id)) {
					acc.newSelectedServiceKiosks.push(kiosk);
				} else {
					acc.oldSelectedServiceKiosks.push({ ...kiosk, selected: false });
				}

				return acc;
			},
			{
				newSelectedServiceKiosks: [] as IKiosk[],
				oldSelectedServiceKiosks: [],
			} as { newSelectedServiceKiosks: IKiosk[]; oldSelectedServiceKiosks: ISaleKiosk[] }
		);

		return {
			id: storeId,
			name,
			saleKiosks: [...selectedSaleKiosks, ...oldSelectedServiceKiosks],
			serviceKiosks: newSelectedServiceKiosks,
		};
	});
}

function resetSelectedSalesKiosksInStores(kmsStores: IKmsStore[]) {
	return kmsStores.map((store) => ({ ...store, saleKiosks: store.saleKiosks.map((kiosk) => ({ ...kiosk, selected: false })) }));
}

function joinStores(kmsStoresFromProvider: IKmsStore[], kmsStores: IKmsStore[]) {
	return (kmsStoresFromProvider ?? []).reduce((acc, store) => {
		if (acc.some(({ id }) => id === store.id)) {
			return acc;
		}
		return [...acc, store];
	}, kmsStores);
}

function isAnyKioskSelected(stores: IKmsStore[]) {
	return (stores ?? []).some(({ saleKiosks, serviceKiosks }) =>
		or(
			Boolean(serviceKiosks?.length),
			saleKiosks.some(({ selected }) => Boolean(selected))
		)
	);
}

function splitKiosksByType(kmsStores: IKmsStore[]) {
	return kmsStores.reduce<{ saleKiosks: TSwitchableItem[]; serviceKiosks: TSwitchableItem[] }>(
		(acc, { id: storeId, saleKiosks, serviceKiosks }) => {
			const newSaleKiosks = saleKiosks
				.filter((item) => item.selected)
				.map((item) => ({ label: `(${item.id}) ${item.name}`, storeId, value: item }));

			const newServiceKiosks = serviceKiosks.map((item) => ({
				label: `(${item.id}) ${item.name}`,
				storeId,
				value: item,
			}));

			return { saleKiosks: [...acc.saleKiosks, ...newSaleKiosks], serviceKiosks: [...acc.serviceKiosks, ...newServiceKiosks] };
		},
		{ saleKiosks: [], serviceKiosks: [] }
	);
}

function getNewKiosks(kiosks: TSwitchableItem[], storeId: number): IKiosk[];
function getNewKiosks(kiosks: TSwitchableItem[], storeId: number, isSaleType: true): ISaleKiosk[];
function getNewKiosks(kiosks: TSwitchableItem[], storeId: number, isSaleType = false) {
	return kiosks
		.filter((item) => storeId === item.storeId)
		.map(({ value: { id, name } }) => (isSaleType ? { id, name, selected: true } : { id, name }));
}

function getUniqueSaleKiosks(newSaleKiosks: ISaleKiosk[], oldSaleKiosks: ISaleKiosk[], newServiceKiosks: IKiosk[]) {
	const oldKiosksIdsWithoutServiceKiosks = oldSaleKiosks
		.map((kiosk) => kiosk.id)
		.filter((id) => !newServiceKiosks.some((serviceKiosk) => serviceKiosk.id === id));

	const uniqueIds = Array.from(new Set([...newSaleKiosks.map((kiosk) => kiosk.id), ...oldKiosksIdsWithoutServiceKiosks]));

	return uniqueIds
		.map((id) => newSaleKiosks?.find((kiosk) => kiosk.id === id) ?? oldSaleKiosks?.find((kiosk) => kiosk.id === id) ?? false)
		.filter((item) => item !== false);
}

function isThereAnyNewChangeInStoreDropdown(currentSelectedStores: IKmsStore[], newSelectedStores: IKmsStore[]) {
	if (currentSelectedStores.length !== newSelectedStores.length) {
		return true;
	}

	const sortedCurrentSelectedStores = getSortedOptions(currentSelectedStores, "id");
	const sortedNewSelectedStores = getSortedOptions(newSelectedStores, "id");

	return sortedCurrentSelectedStores.some((store, index) => store.id !== sortedNewSelectedStores[index].id);
}

function isThereAnyNewChangeInKioskDropdown(
	currentSelectedKiosks: { storeId: number; kioskId: number }[],
	newSelectedKiosks: { storeId: number; kioskId: number }[]
) {
	if (currentSelectedKiosks.length !== newSelectedKiosks.length) {
		return true;
	}

	return currentSelectedKiosks.some(({ kioskId, storeId }) => {
		return !newSelectedKiosks.some(({ kioskId: newKioskId, storeId: newStoreId }) => newKioskId === kioskId && newStoreId === storeId);
	});
}

function isThereAnyNewSwitchBetweenSides(
	currentSides: { saleKiosks: TSwitchableItem[]; serviceKiosks: TSwitchableItem[] },
	newSides: { saleKiosks: TSwitchableItem[]; serviceKiosks: TSwitchableItem[] }
) {
	/* Only check one side because if something changed in one side means the it must be in the other side */
	if (currentSides.saleKiosks.length !== newSides.saleKiosks.length) {
		return true;
	}

	return currentSides.saleKiosks.some(({ storeId, value: { id } }) => {
		return !newSides.saleKiosks.some(({ storeId: newStoreId, value: { id: newId } }) => newId === id && newStoreId === storeId);
	});
}

export {
	getKioskOptions,
	defineKiosksTypes,
	updateSelectedKiosksInStores,
	joinStores,
	isAnyKioskSelected,
	splitKiosksByType,
	getNewKiosks,
	getUniqueSaleKiosks,
	isThereAnyNewChangeInStoreDropdown,
	isThereAnyNewChangeInKioskDropdown,
	isThereAnyNewSwitchBetweenSides,
};
